import type { RTCSession } from 'jssip';
import { Emitter } from './events';
import type { Logger } from './logger';
import type { MediaController } from './media';
import { reasonOf } from './util';
import type { FailureReason } from './types';

/** Events a {@link Call} emits. */
export interface CallEvents {
  /** Remote party is ringing / early media. */
  ringing: { statusCode?: number };
  /** The call was answered (media flowing). */
  accepted: void;
  /** The call ended after being established. */
  ended: FailureReason;
  /** The call never established (rejected, busy, no answer, error…). */
  failed: FailureReason;
  hold: void;
  unhold: void;
  muted: void;
  unmuted: void;
}

/**
 * One call leg. Wraps a JsSIP `RTCSession` and exposes the phone controls a UI
 * needs, plus rich *local* events (see the SDK plan §4.4 — platform-truth events
 * like cost and recordings are consumed on the developer's backend, not here).
 */
export class Call extends Emitter<CallEvents> {
  /** `'incoming'` or `'outgoing'`. */
  readonly direction: 'incoming' | 'outgoing';
  /** The far party's SIP URI, best-effort. */
  readonly remoteIdentity: string;

  constructor(
    private session: RTCSession,
    private media: MediaController,
    private log: Logger,
    private pcConfig: RTCConfiguration,
    private iceGatheringTimeout: number,
  ) {
    super();
    this.direction = session.direction;
    this.remoteIdentity = safeIdentity(session);
    this.wire();
    this.capIceGathering();
  }

  /**
   * Send the offer/answer as soon as we have a usable candidate, instead of
   * waiting for ICE gathering to fully complete — which on UDP-restricted
   * networks means the full ~39.5s RFC 5389 STUN timeout.
   *
   * JsSIP's `icecandidate` event hands us a `ready()` that ends gathering early.
   * We call it the instant a TURN **relay** candidate appears: a relay is a
   * guaranteed connectivity path, and by the time it's gathered the faster
   * server-reflexive (direct) candidate is almost always already in the pool
   * too — so the offer still carries the direct path when one exists. This
   * connects in ~the TURN-allocate time (sub-second when coturn is nearby)
   * rather than a fixed cap. `iceGatheringTimeout` is only the fallback for when
   * no relay ever arrives (e.g. TURN misconfigured); on good networks JsSIP's own
   * gathering-complete fires first and this never matters.
   */
  private capIceGathering(): void {
    if (this.iceGatheringTimeout <= 0) return;
    let ready: (() => void) | undefined;
    let settled = false;
    const proceed = (why: string): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      this.log.debug('ICE: proceeding —', why);
      try {
        ready?.();
      } catch {
        /* ignore */
      }
    };
    const timer = setTimeout(() => proceed('gathering cap'), this.iceGatheringTimeout);
    this.session.on('icecandidate', (e) => {
      const ev = e as { ready?: () => void; candidate?: RTCIceCandidate | null };
      ready = ev?.ready;
      if (ev?.candidate?.candidate?.includes(' typ relay')) proceed('relay candidate');
    });
    // Call already progressing → gathering produced a usable offer; stop the cap.
    const clear = (): void => {
      settled = true;
      clearTimeout(timer);
    };
    this.session.on('progress', clear);
    this.session.on('accepted', clear);
    this.session.on('failed', clear);
    this.session.on('ended', clear);
  }

  private wire(): void {
    const s = this.session;
    if (s.connection) this.attachPeer(s.connection);
    s.on('peerconnection', (e) => this.attachPeer((e as { peerconnection: RTCPeerConnection }).peerconnection));
    s.on('progress', (e) => this.emit('ringing', { statusCode: (e as { response?: { status_code?: number } })?.response?.status_code }));
    s.on('accepted', () => this.emit('accepted'));
    s.on('ended', (e) => { this.media.detach(); this.emit('ended', reasonOf(e)); });
    s.on('failed', (e) => { this.media.detach(); this.emit('failed', reasonOf(e)); });
    s.on('hold', () => this.emit('hold'));
    s.on('unhold', () => this.emit('unhold'));
    s.on('muted', () => this.emit('muted'));
    s.on('unmuted', () => this.emit('unmuted'));
  }

  private attachPeer(pc: RTCPeerConnection): void {
    pc.addEventListener('track', (ev) => {
      const stream = ev.streams[0];
      if (stream) this.media.attachStream(stream);
    });
    pc.addEventListener('iceconnectionstatechange', () => this.log.debug('ICE state', pc.iceConnectionState));
  }

  /** Answer an incoming call. */
  answer(): void {
    if (this.direction !== 'incoming') throw new Error('Only incoming calls can be answered.');
    this.session.answer({ mediaConstraints: this.media.audioConstraints(), pcConfig: this.pcConfig });
  }

  /** Decline an incoming call (SIP 486 Busy Here). */
  reject(): void {
    this.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
  }

  /** Hang up an active or outgoing call. */
  hangup(): void {
    this.session.terminate();
  }

  hold(): void {
    this.session.hold();
  }

  unhold(): void {
    this.session.unhold();
  }

  /** Mute the local microphone (audio only). */
  mute(): void {
    this.session.mute({ audio: true });
  }

  unmute(): void {
    this.session.unmute({ audio: true });
  }

  /** Send a DTMF tone (e.g. navigating an IVR). */
  sendDigit(tone: string): void {
    this.session.sendDTMF(tone);
  }

  get isOnHold(): boolean {
    const h = this.session.isOnHold();
    return h.local || h.remote;
  }

  get isMuted(): boolean {
    return this.session.isMuted().audio;
  }
}

function safeIdentity(s: RTCSession): string {
  try {
    return s.remote_identity?.uri?.toString() ?? '';
  } catch {
    return '';
  }
}
